// @flow
import * as React from 'react';

import {
  selectTaxesDisplaySetting,
  useRequireAuthLink,
} from '@gandi/authentication';
import {
  selectCartCurrency,
  selectCartTotal,
  selectCartUUID,
  selectCountItems,
} from '@gandi/cart';

import { useTranslator } from '@gandi/translate';
import {
  Base,
  Button,
  Drawer,
  Text,
  TextPlaceholder,
  Tooltip,
  TooltipContainer,
  useDrawerState,
} from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useCartTrayVisible } from 'src/hooks/useCartTrayVisible';
import { selectCurrentBU } from 'src/modules/organization/selectors';

import { selectFirstPurchaseUrl } from '../Cart/AccountChoice/Presenter';
import { selectShouldRedirectToAccountForm } from '../Cart/AccountChoice/modules/selectors';

import styles from './CartTray.scss';

function usePrice():
  | { formatted: string, mention: string, price: string }
  | { formatted: React.MixedElement } {
  const { __, formatPrice } = useTranslator();
  const currency = useAppSelector(selectCartCurrency);
  const totalProduct = useAppSelector(selectCartTotal);
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);
  const currentBU = useAppSelector(selectCurrentBU);

  const hasTaxes =
    !!totalProduct?.taxes?.length && totalProduct.taxes?.[0].rate !== 0;
  const showExcludeVatMention = hasTaxes && !displayTaxes;
  const withoutTaxes = showExcludeVatMention && currentBU !== 3;

  if (!currency) {
    return {
      formatted: <TextPlaceholder />,
    };
  }

  const allPricesInError =
    totalProduct.beforeTaxes === 0 && totalProduct.has_pricing_errors;
  if (allPricesInError) {
    return {
      formatted: <TextPlaceholder />,
    };
  }

  const price = withoutTaxes
    ? formatPrice(totalProduct.beforeTaxes, currency)
    : formatPrice(totalProduct.afterTaxes, currency);

  const mention = withoutTaxes ? __('excl. taxes') : __('incl. taxes');

  return {
    price,
    mention,
    formatted: `${price} ${mention}`,
  };
}

function TotalTooltipContainer({ children }: { children: React.Node }) {
  const { __ } = useTranslator();
  const cartTotal = useAppSelector(selectCartTotal);

  if (!cartTotal.has_pricing_errors) return children;

  return (
    <TooltipContainer
      id="cart-tray-total-tooltip"
      renderReference={getReferenceProps => (
        <Base {...getReferenceProps()} tagName="span">
          {children}
        </Base>
      )}
    >
      {getTooltipProps => (
        <Tooltip {...getTooltipProps()}>
          {__('Continue to review products that require your attention.')}
        </Tooltip>
      )}
    </TooltipContainer>
  );
}

export function CartTray(): React.MixedElement {
  const { formatted } = usePrice();
  const drawer = useDrawerState();
  const { __ } = useTranslator();

  const { cartTrayDisplayed } = useCartTrayVisible();

  const productsCount = useAppSelector(selectCountItems);
  const cartId = useAppSelector(selectCartUUID);

  const redirectToAccountForm = useAppSelector(
    selectShouldRedirectToAccountForm,
  );

  const firstPurchaseUrl = useAppSelector(selectFirstPurchaseUrl);
  const continueUrl = redirectToAccountForm
    ? firstPurchaseUrl
    : `/cart/${cartId}`;

  const linkProps = useRequireAuthLink(continueUrl);

  function handleClick() {
    drawer.close();
  }

  React.useEffect(() => {
    if (cartTrayDisplayed) {
      drawer.show();
    } else {
      drawer.close();
    }
  }, [drawer, cartTrayDisplayed]);

  return (
    <Drawer {...drawer} position="bottom" className={styles.cartTray}>
      <Base flex flexJustifyEnd className={styles.mainWrapperCartTray}>
        <Base flex gap="small" flexAlignItemsCenter>
          <Text color="weak" size="big">
            {__('%(count)s product', { count: productsCount })}
          </Text>{' '}
          <Text size="big" weight="semibold">
            <TotalTooltipContainer>{formatted}</TotalTooltipContainer>
          </Text>
        </Base>

        <Button {...linkProps} iconAfter="arrowRight" onClick={handleClick}>
          {__('<for-translators-use>Go to cart</for-translators-use>Checkout')}
        </Button>
      </Base>
    </Drawer>
  );
}
